import React, { useState, useEffect } from 'react';
import { homeworkService } from '../../lib/services/homework';

const TimetableSystem = ({ selectedItem, timeSlots, dayNames, dayKeys, onTimetableClick, onBack }) => {
  const [timetableData, setTimetableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInfoPanels, setShowInfoPanels] = useState(false); // Toggle for right-hand side panels

  const loadTimetableData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await homeworkService.getClassTimetable(selectedItem.id || selectedItem.class_id);
      setTimetableData(data);
    } catch (err) {
      console.error('Error loading timetable data:', err);
      setError('載入時間表時發生錯誤');
    } finally {
      setLoading(false);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem) {
      loadTimetableData();
    }
  }, [selectedItem, loadTimetableData]);

  const getSubjectTypeColor = (type) => {
    const colors = {
      'core': 'bg-blue-50 border-blue-200',
      'elective': 'bg-purple-50 border-purple-200'
    };
    return colors[type] || 'bg-gray-50 border-gray-200';
  };

  const getSubjectTypeLabel = (type) => {
    const labels = {
      'core': '核心科目',
      'elective': '選修科目'
    };
    return labels[type] || '一般科目';
  };

  if (loading) {
    return (
      <div className="timetable-system">
        <div className="timetable-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回列表
          </button>
          <h2>{selectedItem?.class_name || selectedItem?.teacher_name || selectedItem?.student_name} - 時間表</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入時間表中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timetable-system">
        <div className="timetable-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回列表
          </button>
          <h2>{selectedItem?.class_name || selectedItem?.teacher_name || selectedItem?.student_name} - 時間表</h2>
        </div>
        <div className="error-container">
          <p>❌ {error}</p>
          <button onClick={loadTimetableData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="timetable-system">
      <div className="timetable-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回列表
        </button>
        <h2>{selectedItem?.class_name || selectedItem?.teacher_name || selectedItem?.student_name} - 時間表</h2>
        <div className="timetable-controls">
          <div className="timetable-legend">
            <span className="legend-item">
              <span className="legend-color core"></span>
              核心科目
            </span>
            <span className="legend-item">
              <span className="legend-color elective"></span>
              選修科目
            </span>
          </div>
          <button 
            className={`info-toggle-btn ${showInfoPanels ? 'active' : ''}`}
            onClick={() => setShowInfoPanels(!showInfoPanels)}
            title={showInfoPanels ? '隱藏說明面板' : '顯示說明面板'}
          >
            {showInfoPanels ? '隱藏說明' : '顯示說明'}
          </button>
        </div>
      </div>

      <div className={`timetable-container ${showInfoPanels ? 'with-info' : 'without-info'}`}>
        <div className="timetable-grid">
          <table className="timetable-table">
            <thead>
              <tr>
                <th className="time-header">時間</th>
                {dayNames.map((day, index) => (
                  <th key={day} className="day-header">
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((timeSlot, slotIndex) => (
                <tr key={timeSlot} className={slotIndex % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td className="time-cell">
                    {timeSlot}
                  </td>
                  {dayKeys.map((dayKey, dayIndex) => {
                    const lesson = timetableData?.[dayKey]?.[slotIndex];
                    return (
                      <td 
                        key={`${dayKey}-${slotIndex}`} 
                        className={`lesson-cell ${lesson ? 'has-lesson' : 'empty-lesson'} ${lesson ? getSubjectTypeColor(lesson.type) : ''}`}
                        onClick={() => lesson && onTimetableClick(slotIndex, dayIndex)}
                      >
                        {lesson ? (
                          <div className="lesson-content">
                            <div className="lesson-subject">{lesson.subject}</div>
                            <div className="lesson-teacher">{lesson.teacher}</div>
                            <div className="lesson-classroom">📍 {lesson.classroom}</div>
                            <div className="lesson-type">
                              {getSubjectTypeLabel(lesson.type)}
                            </div>
                          </div>
                        ) : (
                          <div className="empty-lesson-content">
                            <span className="empty-text">空堂</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showInfoPanels && (
          <div className="timetable-info">
            <div className="info-section">
              <h3>時間表說明</h3>
              <ul>
                <li>點擊任何有課程的時段可查看家課</li>
                <li>藍色背景表示核心科目</li>
                <li>紫色背景表示選修科目</li>
                <li>Form 6班級支援選修制度</li>
              </ul>
            </div>

            {selectedItem?.class_level >= 6 && (
              <div className="info-section">
                <h3>選修制度說明</h3>
                <ul>
                  <li><strong>X1選修:</strong> 生物、化學、物理、歷史、地理</li>
                  <li><strong>X2選修:</strong> 生物、化學、物理、歷史、數學延伸</li>
                  <li><strong>跨班制:</strong> 學生根據選修到不同課室上課</li>
                </ul>
              </div>
            )}

            <div className="info-section">
              <h3>家課查看提示</h3>
              <ul>
                <li>點擊任意課節可查看該科目的家課</li>
                <li>系統會顯示當日及相關的家課安排</li>
                <li>支援日曆和清單兩種檢視模式</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimetableSystem;
